// @flow
import ReactGA from 'react-ga';
import authApiService from '../services/auth-api-service';
import history from '../history';
import { bootIntercomForUser } from '../services/intercom-service';
import type { AddressType } from '../types/address';
import type { UserType, IDCheck } from '../types/user';
import { addBanner } from './banners';
import type { UserAction, ThunkAction, GetState, Dispatch } from '../types/actions';
import { getRouteTFunction } from '../i18n';

function requestUser(): UserAction {
  return {
    type: 'REQUEST_USER'
  };
}

function redirected(): UserAction {
  return {
    type: 'REDIRECTED',
    redirected: true
  };
}

function navigated(): UserAction {
  return {
    type: 'REDIRECTED',
    redirected: false
  };
}

function receiveIDcheck(idCheck: any): UserAction {
  return {
    type: 'RECEIVE_ID_CHECK',
    idCheck
  };
}

function loadUser(): UserAction {
  return {
    type: 'LOAD_USER'
  };
}

function postUser(): UserAction {
  return {
    type: 'POST_USER'
  };
}
function postProfile(): UserAction {
  return {
    type: 'POST_PROFILE'
  };
}
function postAddress(): UserAction {
  return {
    type: 'POST_ADDRESS'
  };
}
function postPhotoIdentification(): UserAction {
  return {
    type: 'POST_PHOTO_IDENTIFICATION'
  };
}
function receivePhotoIdentification(): UserAction {
  return {
    type: 'RECEIVE_PHOTO_IDENTIFICATION'
  };
}
function photoIdentificationError(error): UserAction {
  return {
    type: 'PHOTO_IDENTIFICATION_ERROR',
    error
  };
}
function postIDVerification(): UserAction {
  return {
    type: 'POST_ID_VERIFICATION'
  };
}
function idVerificationError(error): UserAction {
  return {
    type: 'ID_VERIFICATION_ERROR',
    error
  };
}
function receiveIDVerification(): UserAction {
  return {
    type: 'RECEIVE_ID_VERIFICATION'
  };
}
function receiveUser(json: { user: ?UserType }): UserAction {
  return {
    type: 'RECEIVE_USER',
    user: json.user || {},
    empty: !json.user
  };
}
function emailStatusUpdated() {
  return {
    type: 'EMAIL_STATUS_UPDATED'
  };
}

function userError(error: string): UserAction {
  return {
    type: 'USER_ERROR',
    error
  };
}

function createUser(email, redirect = true) {
  return (dispatch) => {
    dispatch(postUser());
    return authApiService.post('/onboarding/create_user', {
      user: {
        email
      } })
      .then((response: { user: ?UserType }) => {
        dispatch(receiveUser(response));
        if (redirect) {
          history.push('/');
        }
      })
      .catch((err) => (
        dispatch(userError(err.message))
      ));
  };
}

function fetchUser(include: string[] = []) {
  let qs = '';
  if (include.length > 0) {
    qs = `?include=${include.join(',')}`;
  }
  return (dispatch) => {
    dispatch(requestUser());
    authApiService.get(`/user${qs}`)
      .then((response: { user: ?UserType }) => {
        if (response) {
          bootIntercomForUser(response.user);
          return dispatch(receiveUser(response));
        }
        return dispatch(userError('Failed to retrieve user'));
      });
  };
}

function createProfile(user: UserType) {
  return (dispatch) => {
    dispatch(postProfile());
    return authApiService
      .post('/onboarding/create_profile', { user })
      .then((response: { user: ?UserType }) => {
        dispatch(receiveUser(response));
        history.push('/');
      })
      .catch((error) => dispatch(userError(error)));
  };
}

function createAddress(address: AddressType) {
  return (dispatch) => {
    dispatch(postAddress());
    return authApiService
      .post('/onboarding/create_address', { address })
      .then((response: { user: ?UserType }) => {
        dispatch(receiveUser(response));
        history.push('/');
      })
      .catch((error) => dispatch(userError(error)));
  };
}
function createPhotoIdentification(image: Blob) {
  return (dispatch) => {
    dispatch(postPhotoIdentification());
    const data = new FormData();
    data.append('photo_id', image);
    const routesTFunction = getRouteTFunction();
    return authApiService
      .upload('/onboarding/create_photo_id', image, 'photo_id', {})
      .then((response) => {
        dispatch(receivePhotoIdentification(response));
        history.push(routesTFunction('redirectPaths./user/identity/id-verification'));
      })
      .catch((error) => {
        dispatch(photoIdentificationError(error));
      });
  };
}
function createIDVerification(image) {
  return (dispatch) => {
    dispatch(postIDVerification());
    return authApiService
      .upload('/onboarding/create_photo_id_verification', image, 'photo_id_verification', {})
      .then((response: { user: ?UserType }) => {
        ReactGA.pageview('/user/onboarding-complete');
        dispatch(receiveIDVerification(response));
        dispatch(fetchUser(['onboarding_state']));
      })
      .catch((error) => dispatch(idVerificationError(error)));
  };
}


function updateEmailStatus() {
  return (dispatch) => {
    dispatch(postUser());
    authApiService
      .put('/onboarding/verify_email')
      .then((response: { user: UserType }) => {
        dispatch(emailStatusUpdated());
        return dispatch(receiveUser(response));
      })
      .catch((error) => dispatch(userError(error)));
  };
}

function fetchIDCheck() {
  return (dispatch) => {
    dispatch(loadUser());
    return authApiService
      .get('/admin_id_check')
      .then((response: { admin_id_check: IDCheck }) => dispatch(receiveIDcheck(response.admin_id_check)))
      .catch((error) => dispatch(userError(error)));
  };
}

function updateUser(user: UserType) {
  return (dispatch, getState) => {
    dispatch(loadUser());
    return authApiService
      .put('/user', user)
      .then((response: { user: UserType }) => {
        const updatedUser = {
          ...getState().user.data,
          ...response.user
        };
        return dispatch(receiveUser({ user: updatedUser }));
      })
      .catch((error) => dispatch(userError(error)));
  };
}

function submitInsuranceNumber(insuranceNumber: string): ThunkAction {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch(loadUser());
    return authApiService
      .post('/user/insurance_documents', {
        user: {
          ...getState().user.data,
          insurance_number: insuranceNumber
        }
      })
      .then((response: { user: UserType }) => {
        const updatedUser = {
          ...getState().user.data,
          ...response.user
        };
        return dispatch(receiveUser({ user: updatedUser }));
      })
      .catch((error) => dispatch(userError(error)));
  };
}

const INITIAL_REDIRECT = {
  no_state: { // what gets hit if there was no user or user did not have an onboarding state
    pathname: '/login'
  },
  UserCreated: {
    pathname: '/user/info'
  },
  QuestionnaireCompleted: {
    pathname: '/dashboard'
  },
  ProfileCreated: {
    pathname: '/dashboard'
  },
  AddressCreated: {
    pathname: '/dashboard'
  },
  PhotoIDUploaded: {
    pathname: '/dashboard'
  },
  AwaitingIDVerification: {
    banner: 'idVerificationPending',
    pathname: '/dashboard'
  },
  IDRejected: {
    banner: 'idRejected',
    pathname: '/dashboard'
  },
  Verified: {
    pathname: '/dashboard'
  },
  LegacyUnverified: {
    banner: 'legacyUnverified',
    pathname: '/dashboard'
  },
  Completed: {
    pathname: '/dashboard'
  },
  AwaitingEmailVerification: (user, dispatch) => {
    dispatch(updateEmailStatus());
    return {
      pathname: '/dashboard'
    };
  },
  AwaitingVerification: (user, dispatch) => {
    dispatch(updateEmailStatus());
    if (!user.emailCheckComplete) {
      return {
        pathname: '/dashboard'
      };
    }
    return {
      banner: 'idVerificationPending',
      pathname: '/dashboard'
    };
  }
};

function routeUser() {
  return (dispatch, getState) => {
    const user = getState().user.data;
    let next;
    if (user && user.onboarding_state) {
      next = INITIAL_REDIRECT[user.onboarding_state.state];
      if (typeof next === 'function') {
        next = next(user, dispatch);
      }
      if (next.banner) {
        dispatch(addBanner(next.banner));
      }
    } else {
      next = INITIAL_REDIRECT.no_state;
    }
    if (next.pathname) {
      const routesTFunction = getRouteTFunction();
      history.push({
        pathname: routesTFunction(`redirectPaths.${next.pathname}`)
      });
      dispatch(redirected());
    }
  };
}

export default {
  redirected,
  navigated,
  createUser,
  createProfile,
  createAddress,
  createPhotoIdentification,
  createIDVerification,
  fetchUser,
  updateEmailStatus,
  fetchIDCheck,
  updateUser,
  submitInsuranceNumber,
  routeUser
};



// WEBPACK FOOTER //
// ./src/actions/user.js